import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NumbersAPI } from 'api/cards';
import LoadingSpinner from 'components/loader/LoadingSpinner';
import Button from 'components/utils/Button';
import Input from 'components/utils/Input';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import RequiredMark from 'components/utils/requiredMark';
import { Skeleton } from 'components/utils/Skeleton';
import { StaffOnlyBadgeType, StaffOnlyWrapper } from 'components/utils/StaffOnlyWrapper';
import { Body2, Caption2, H4 } from 'components/utils/typo';
import { X } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MODULE_TYPES } from '../../../constants';

interface CustomNumberFormVariable {
  name: string;
  unit: string;
  lower_limit?: number | null;
  upper_limit?: number | null;
  local_id: string;
  calculator?: number | string;
  module?: number | string;
}

interface CustomNumbersFormProps {
  customNumberId?: number | null;
  onSave: (data) => void;
  onCancel: () => void;
}

export const CustomNumbersForm = ({ customNumberId, onSave, onCancel }: CustomNumbersFormProps) => {
  const { module } = useContext(ModuleContext);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const defaultValues: CustomNumberFormVariable = {
    name: '',
    unit: '',
    lower_limit: null,
    upper_limit: null,
    calculator: module?.type === MODULE_TYPES.CALCULATOR ? module.id : '',
    module: module?.type === MODULE_TYPES.ALGO ? module.id : '',
    local_id: '',
  };

  const {
    watch,
    register,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm<CustomNumberFormVariable>({ defaultValues });

  const { data, isLoading } = useQuery({
    queryKey: ['customNumber', customNumberId],
    queryFn: NumbersAPI.getCustomNumber,
    enabled: !!customNumberId,
  });

  useEffect(() => {
    if (!data) return;
    reset({
      name: data.name,
      unit: data.unit,
      lower_limit: data.lower_limit,
      upper_limit: data.upper_limit,
      local_id: data.local_id || '',
    });
  }, [data]);

  const { isPending, mutateAsync } = useMutation({
    mutationFn: !customNumberId
      ? NumbersAPI.postCustomNumber
      : (data: CustomNumberFormVariable) => NumbersAPI.patchCustomNumber(customNumberId, data),
  });

  const queryClient = useQueryClient();

  const onSubmit = async (data: CustomNumberFormVariable) => {
    if (isPending || !module) return;
    const res = await mutateAsync(data);
    queryClient.invalidateQueries({ queryKey: [module.type, module.id, 'customNumbers'] });
    onSave(res);
  };

  return (
    <>
      <form className='flex h-full flex-col' onSubmit={handleSubmit(onSubmit)}>
        <div className='grow overflow-y-auto'>
          <div className='sticky top-0 z-10 flex items-center justify-between bg-white pl-4 pr-3 pt-[20px]'>
            <H4>Custom Numbers</H4>
            <div
              className='cursor-pointer'
              onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
            >
              <X size={20} />
            </div>
          </div>
          <div className='mt-4 px-4'>
            <div className='space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Name</Body2>
                <RequiredMark />
              </div>
              {isLoading ? (
                <Skeleton />
              ) : (
                <>
                  <Input
                    {...register('name', { required: true, maxLength: 70 })}
                    required
                    maxLength={70}
                  />
                  <Caption2 className='flex justify-end text-gray-text-weak'>
                    {watch('name').length}/70
                  </Caption2>
                </>
              )}
            </div>
            <div className='mt-1 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Unit</Body2>
                <RequiredMark />
              </div>
              {isLoading ? (
                <Skeleton />
              ) : (
                <>
                  <Input
                    {...register('unit', { required: true, maxLength: 70 })}
                    required
                    maxLength={70}
                  />
                  <Caption2 className='flex justify-end text-gray-text-weak'>
                    {watch('unit').length}/70
                  </Caption2>
                </>
              )}
            </div>
            <div className='mt-1 space-y-1'>
              <Body2 className='text-gray-900'>Lower Limit</Body2>
              {isLoading ? (
                <Skeleton />
              ) : (
                <Input
                  type='number'
                  step={0.001}
                  {...register('lower_limit', { setValueAs: (v) => (v === '' ? null : v) })}
                />
              )}
            </div>
            <div className='mt-6 space-y-1'>
              <Body2 className='text-gray-900'>Upper Limit</Body2>
              {isLoading ? (
                <Skeleton />
              ) : (
                <Input
                  type='number'
                  step={0.001}
                  {...register('upper_limit', { setValueAs: (v) => (v === '' ? null : v) })}
                />
              )}
            </div>
            <StaffOnlyWrapper type={StaffOnlyBadgeType.STAFF}>
              <div className='mt-6 space-y-1'>
                <Body2 className='text-gray-900'>Local ID</Body2>
                {isLoading ? (
                  <Skeleton />
                ) : (
                  <>
                    <Input {...register('local_id', { maxLength: 70 })} maxLength={70} />
                    <Caption2 className='flex justify-end text-gray-text-weak'>
                      {watch('local_id').length}/70
                    </Caption2>
                  </>
                )}
              </div>
            </StaffOnlyWrapper>
          </div>
        </div>
        <div className='shrink-0'>
          <div className='flex justify-end gap-2 px-[12px] pb-[22px] pt-[12px]'>
            <div className='flex'>
              <Button.Reverse
                type='button'
                onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
                disabled={isPending}
              >
                Cancel
              </Button.Reverse>
            </div>
            <div className='flex w-[80px]'>
              <Button disabled={isPending}>{isPending ? <LoadingSpinner /> : 'Save'}</Button>
            </div>
          </div>
        </div>
      </form>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={onCancel}
      />
    </>
  );
};
